import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useOutsideClickHandler } from "../../../../Hooks/useOutsideClick";
import {
  closeSettingsModal,
  fetchSellerMembers,
} from "../../../../Features/settingsSlice";
import Cross from "../../../../Assets/Modals/closeIcon.svg";
import ChevronDown from "../../../../Assets/ChevronDownLargeDarkMode.svg";
import TickMark from "../../../../Assets/Modals/TickMark.svg";
import { useOutsideClickForTwoRef } from "../../../../Hooks/useOutsideClickForTwoRef";
import { verifyEmail } from "../../../../lib/utils/utils";
import { v4 as uuidv4 } from "uuid";
import CrossSmall from "../../../../Assets/CrossSmall.svg";
import { toast } from "react-toastify";
import { showToast } from "../../../../Components/Toaster/Toaster";
import { inviteSellerMember } from "../../../../lib/utils/apiCalls";

const rolesList = [
  {
    id: "admin",
    role: "Admin",
  },
  {
    id: "store-manager",
    role: "Store Manager",
  },
  {
    id: "inventory-manager",
    role: "Inventory Manager",
  },
  {
    id: "lens-manager",
    role: "Lens Manager",
  },
];

function InviteMembers() {
  const dispatch = useDispatch();
  const wrapperRef = useRef(null);

  const dropdownRef = useRef(null);
  const dropdownButtonRef = useRef(null);

  useOutsideClickForTwoRef(dropdownRef, dropdownButtonRef, () => {
    setDropdownOpen(false);
  });

  function handleDropdownToggle() {
    setDropdownOpen(!dropdownOpen);
  }

  const [emails, setEmails] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [selectedRoles, setSelectedRoles] = useState([]);

  const [buttonAvailable, setButtonAvailable] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const defaultFormErrors = {
    email: "",
    roles: "",
  };

  const [formErrors, setFormErrors] = useState(defaultFormErrors);

  function handleChange(e) {
    setInputValue(e.target.value);
  }

  function removeEmail(id) {
    const filteredList = emails.filter((item) => item.id !== id);
    setEmails(filteredList);
  }

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      if (verifyEmail(inputValue)) {
        if (emails.some((obj) => obj.email === inputValue)) {
          setFormErrors({
            ...formErrors,
            email: "Email already present!",
          });
        } else {
          setEmails((prev) => [...prev, { email: inputValue, id: uuidv4() }]);
          setInputValue("");
          setFormErrors({
            ...formErrors,
            email: "",
          });
          return;
        }
      } else {
        setFormErrors({
          ...formErrors,
          email: "Wrong email format!",
        });
        return;
      }
    }
  };

  useOutsideClickHandler(wrapperRef, () => {
    closeForm();
  });

  function closeForm() {
    dispatch(closeSettingsModal());
  }

  function handleRoleClick(e, role) {
    e.stopPropagation();
    if (!selectedRoles.includes(role)) {
      setSelectedRoles([...selectedRoles, role]);
    } else {
      const filteredRoles = selectedRoles.filter((item) => item.id !== role.id);
      setSelectedRoles(filteredRoles);
    }
  }

  useEffect(() => {
    if (emails.length > 0 && selectedRoles.length > 0) {
      setButtonAvailable(true);
    } else {
      setButtonAvailable(false);
    }
  }, [emails, selectedRoles]);

  async function handleSendInvite() {
    const validationErrors = {};

    if (!emails.length) {
      validationErrors.email = "No email(s) added";
    }

    if (!selectedRoles.length) {
      validationErrors.roles = "No role(s) added";
    }

    if (Object.keys(validationErrors).length === 0) {
      setFormErrors(defaultFormErrors);

      const invitePromises = emails.map((email) =>
        inviteSellerMember({
          email: email.email,
          role: selectedRoles.map((role) => role.role),
        })
      );

      const inviteAllPromise = Promise.all(invitePromises);

      toast.promise(inviteAllPromise, {
        pending: {
          render() {
            return "Inviting selected emails...";
          },
          className: "toast-pending",
        },
        success: {
          render() {
            return "Invites sent.";
          },
        },
      });

      try {
        await inviteAllPromise;
        dispatch(fetchSellerMembers());
        closeForm();
      } catch (err) {
        showToast(
          "Failed to invite some or all emails. Please try again.",
          "error"
        );
      }
    } else {
      setFormErrors(validationErrors);
    }
  }

  return (
    <div
      ref={wrapperRef}
      className="w-[600px] px-6 pt-[18px] pb-6 flex flex-col gap-6 bg-[#FFFFFF] rounded-xl"
    >
      <div className="flex items-center justify-between">
        <h1 className="font-semibold text-[24px] leading-7 text-[#121212]">
          Invite Members
        </h1>

        <button
          onClick={closeForm}
          className="outline-none size-11 p-[10px] flex items-center justify-center"
        >
          <img className="size-6 p-[6px]" src={Cross} alt="" />
        </button>
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <label
            htmlFor="email"
            className="font-medium text-base text-[#121212]"
          >
            Email Address
          </label>

          {formErrors.email && (
            <p className="text-[12px] leading-5 text-[#EF4444]">
              {formErrors.email}
            </p>
          )}
        </div>

        <div className="bg-[#F5F5F5] flex gap-2 flex-wrap border border-[#EAEAEA] rounded-[9px] px-3 py-4">
          {emails.map((item) => {
            return (
              <div
                key={item.id}
                className="w-fit border border-[#EAEAEA] bg-[#FFFFFF] rounded px-2 py-1 flex items-center"
              >
                <p className="font-normal text-[14px] leading-6 text-[#121212]">
                  {item.email}
                </p>

                <button
                  onClick={() => removeEmail(item.id)}
                  className="w-4 h-4 p-[3px] flex items-center justify-center"
                >
                  <img src={CrossSmall} alt="" />
                </button>
              </div>
            );
          })}

          <input
            className="flex-grow font-normal outline-none text-base bg-transparent placeholder:text-[#666666]"
            placeholder="enter email, comma seperated"
            type="email"
            id="email"
            value={inputValue}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
          />
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <p className="font-medium text-base text-[#121212]">Role</p>

        <div className="flex items-center gap-5">
          <div
            ref={dropdownButtonRef}
            onClick={handleDropdownToggle}
            className="relative flex items-center gap-[10px] flex-grow border border-[#EAEAEA] bg-[#F5F5F5] px-3 py-4 rounded-lg cursor-pointer"
          >
            <p className="w-[calc(100%-24px)] font-normal text-base text-[#666666]">
              {selectedRoles.length > 0
                ? selectedRoles.map((role) => role.role).join(", ")
                : "select role"}
            </p>

            <div className="size-6 flex items-center justify-center">
              <img src={ChevronDown} alt="" />
            </div>

            {dropdownOpen && (
              <div
                ref={dropdownRef}
                className="w-full absolute top-14 left-0 rounded-lg overflow-hidden flex flex-col border border-[#EAEAEA] bg-[#FFFFFF] font-medium text-[14px] leading-6 text-[#666666]"
              >
                {rolesList.map((role, index) => {
                  const isSelected = selectedRoles.includes(role);

                  return (
                    <div
                      key={role.id}
                      onClick={(e) => handleRoleClick(e, role)}
                      className={`${
                        index !== 0 && "border-t border-[#EAEAEA]"
                      } p-3 flex items-center gap-[10px]`}
                    >
                      <div
                        className={`size-6 ${
                          !isSelected
                            ? "border border-[#D1D5DB]"
                            : "border-none bg-primaryColor"
                        } rounded-md flex items-center justify-center`}
                      >
                        <img src={isSelected && TickMark} alt="" />
                      </div>

                      <p>{role.role}</p>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <button
            onClick={handleSendInvite}
            className={`outline-none rounded h-14 px-4 py-2 font-semibold text-base text-buttonText ${
              buttonAvailable
                ? "bg-primaryColor hover:bg-primaryAccent"
                : "bg-buttonDisabledBg"
            }`}
          >
            Send Invite
          </button>
        </div>
      </div>
    </div>
  );
}

export default InviteMembers;
